import { c as _c } from "react/compiler-runtime";
import { IconButton } from "@/components/ui/IconButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select";
import type { PaginationResult } from "@/hooks/pagination";
import { ChevronLeft, ChevronRight } from "lucide-react";
import cn from "twsx";
import styles from "./List.module.css";
type PaginationProps = {
  count?: number;
  pagination: PaginationResult;
  pageSizeOptions?: number[];
  className?: string;
};
const defaultPageSizeOptions = [25, 50, 100];
function formatNumber(value: number) {
  return Intl.NumberFormat("en-US").format(value);
}
export default function Pagination(t0) {
  const $ = _c(47);
  const {
    count,
    pagination,
    pageSizeOptions: t1,
    className
  } = t0;
  const pageSizeOptions = t1 === undefined ? defaultPageSizeOptions : t1;
  const {
    page,
    pageSize,
    setPage,
    setPageSize,
    hasPreviousPage,
    hasNextPage,
    totalPages,
    startIndex,
    endIndex,
    isCountKnown
  } = pagination;
  const visibleStart = isCountKnown && count === 0 ? 0 : startIndex + 1;
  const visibleEnd = isCountKnown ? endIndex : startIndex + pageSize;
  let t2;
  if ($[0] !== className) {
    t2 = cn(styles.pagination, "flex h-14 shrink-0 items-center justify-between border-t bg-transparent text-xs text-text-secondary sm:h-10", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== count || $[3] !== isCountKnown || $[4] !== visibleEnd || $[5] !== visibleStart) {
    t3 = <span className="tabular-nums">{isCountKnown ? <><span className="font-medium text-text-primary">{formatNumber(visibleStart)}</span><span>–</span><span className="font-medium text-text-primary">{formatNumber(visibleEnd)}</span><span>{" of "}</span><span className="font-medium text-text-primary">{formatNumber(count ?? 0)}</span></> : <><span className="font-medium text-text-primary">{formatNumber(visibleStart)}</span><span>–</span><span className="font-medium text-text-primary">{formatNumber(visibleEnd)}</span></>}</span>;
    $[2] = count;
    $[3] = isCountKnown;
    $[4] = visibleEnd;
    $[5] = visibleStart;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = String(pageSize);
  let t5;
  if ($[7] !== setPageSize) {
    t5 = value => setPageSize(Number(value));
    $[7] = setPageSize;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SelectTrigger size="sm" className="w-16"><SelectValue /></SelectTrigger>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== pageSizeOptions) {
    t7 = pageSizeOptions.map(_temp);
    $[10] = pageSizeOptions;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <SelectContent align="end">{t7}</SelectContent>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== t8) {
    t9 = <div><Select value={t4} onValueChange={t5}>{t6}{t8}</Select></div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isCountKnown || $[19] !== page || $[20] !== totalPages) {
    t10 = <span className="whitespace-nowrap tabular-nums">{isCountKnown ? <><span className="font-medium text-text-primary">{formatNumber(page)}</span><span>{" / "}</span><span className="font-medium text-text-primary">{formatNumber(totalPages)}</span></> : <><span>{"Page "}</span><span className="font-medium text-text-primary">{formatNumber(page)}</span></>}</span>;
    $[18] = isCountKnown;
    $[19] = page;
    $[20] = totalPages;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== page || $[23] !== setPage) {
    t11 = () => setPage(page - 1);
    $[22] = page;
    $[23] = setPage;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = !hasPreviousPage;
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChevronLeft />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t11 || $[27] !== t12) {
    t14 = <IconButton label="Previous page" onClick={t11} disabled={t12}>{t13}</IconButton>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== page || $[30] !== setPage) {
    t15 = () => setPage(page + 1);
    $[29] = page;
    $[30] = setPage;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = !hasNextPage;
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ChevronRight />;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t15 || $[34] !== t16) {
    t18 = <IconButton label="Next page" onClick={t15} disabled={t16}>{t17}</IconButton>;
    $[33] = t15;
    $[34] = t16;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t14 || $[37] !== t18) {
    t19 = <div className="flex items-center gap-0.5">{t14}{t18}</div>;
    $[36] = t14;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t10 || $[40] !== t19 || $[41] !== t9) {
    t20 = <div className="flex items-center gap-1 sm:gap-3">{t9}{t10}{t19}</div>;
    $[39] = t10;
    $[40] = t19;
    $[41] = t9;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t2 || $[44] !== t20 || $[45] !== t3) {
    t21 = <div className={t2}>{t3}{t20}</div>;
    $[43] = t2;
    $[44] = t20;
    $[45] = t3;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
}
function _temp(option) {
  return <SelectItem key={option} value={String(option)}>{option}</SelectItem>;
}